<template>
  <div class="nft-sorting">
    <div class="sorting-controls">
      <label for="sortBy" class="sort-label">Sort by:</label>
      <select 
        id="sortBy" 
        v-model="currentSortBy" 
        @change="handleSortChange"
        class="sort-select"
      >
        <option 
          v-for="option in availableSortOptions" 
          :key="option.value"
          :value="option.value"
          :disabled="option.requiresSingleCollection && !isSingleCollection"
        >
          {{ option.label }}
        </option>
      </select>

      <label for="sortDirection" class="sort-label">Direction:</label>
      <select 
        id="sortDirection" 
        v-model="currentSortDirection" 
        @change="handleSortChange"
        class="sort-select"
      >
        <option value="DESC">Descending</option>
        <option value="ASC">Ascending</option>
      </select>
    </div>

    <div v-if="debugMode" class="sorting-debug">
      <small>Debug: {{ currentSortBy }} {{ currentSortDirection }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { NFT } from '../types/nft'

// Types
interface SortOption {
  value: string
  label: string
  requiresSingleCollection: boolean
}

// Props
interface Props {
  nfts: NFT[]
  debugMode?: boolean
  enableSorting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  nfts: () => [],
  debugMode: false,
  enableSorting: true
})

// Emits
const emit = defineEmits<{
  'sort-results': [sortedNfts: NFT[]]
  'sort-start': []
  'sort-complete': [message?: string]
}>()

// Reactive state
const currentSortBy = ref('TRANSFERTIME')
const currentSortDirection = ref('DESC')

// Debug function
const debug = (...args: any[]) => {
  if (props.debugMode) {
    console.log('[NFT Sorting]', ...args)
  }
}

// Computed
const isSingleCollection = computed(() => {
  if (props.nfts.length === 0) return false

  const uniqueCollections = new Set(
    props.nfts.map(nft => nft.contract?.address || nft.contractAddress)
  )
  return uniqueCollections.size === 1
})

const availableSortOptions = computed((): SortOption[] => {
  return [
    { value: 'TRANSFERTIME', label: 'Transfer Time', requiresSingleCollection: false },
    { value: 'MINTTIME', label: 'Mint Time', requiresSingleCollection: false },
    { value: 'TOKENID', label: 'Token ID', requiresSingleCollection: true },
    { value: 'COLLECTION', label: 'Collection Name', requiresSingleCollection: false },
    { value: 'NAME', label: 'NFT Name', requiresSingleCollection: false },
    { value: 'ESTIMATED_VALUE', label: 'Estimated Value', requiresSingleCollection: false }
  ]
})

// Methods
const getTokenId = (nft: NFT): string => {
  if (typeof nft.id === 'string') {
    return nft.id
  } else if (nft.id && typeof nft.id === 'object' && 'tokenId' in nft.id) {
    return nft.id.tokenId
  } else {
    return nft.tokenId || ''
  }
}

const validateSorting = (sortBy: string, sortDirection: string): { sortBy: string, sortDirection: string } => {
  const validSortBy = ['TRANSFERTIME', 'TOKENID', 'MINTTIME', 'COLLECTION', 'NAME', 'ESTIMATED_VALUE']
  const validSortDirection = ['ASC', 'DESC']

  // Check if Token ID sorting is valid for current state
  if (sortBy === 'TOKENID' && !isSingleCollection.value) {
    debug('Token ID sorting not valid for multiple collections, switching to TRANSFERTIME')
    sortBy = 'TRANSFERTIME'
  }

  if (!validSortBy.includes(sortBy)) {
    debug('Invalid sortBy, defaulting to TRANSFERTIME')
    sortBy = 'TRANSFERTIME'
  }

  if (!validSortDirection.includes(sortDirection)) {
    debug('Invalid sortDirection, defaulting to DESC')
    sortDirection = 'DESC'
  }

  debug('Validated sorting:', { sortBy, sortDirection })
  return { sortBy, sortDirection }
}

const sortNFTs = (nfts: NFT[], sortBy: string, sortDirection: string): NFT[] => {
  if (nfts.length === 0) {
    debug('No NFTs to sort, returning empty array')
    return nfts
  }

  debug(`Sorting ${nfts.length} NFTs by ${sortBy} ${sortDirection}`)

  const sortedNFTs = [...nfts].sort((a, b) => {
    let aValue: string | number, bValue: string | number

    switch (sortBy) {
      case 'TRANSFERTIME':
        aValue = a.acquiredAt?.blockTimestamp || a.acquiredAt?.timestamp || '1970-01-01T00:00:00Z'
        bValue = b.acquiredAt?.blockTimestamp || b.acquiredAt?.timestamp || '1970-01-01T00:00:00Z'
        break
      case 'TOKENID':
        aValue = parseInt(getTokenId(a) || '0')
        bValue = parseInt(getTokenId(b) || '0')
        break
      case 'MINTTIME':
        aValue = a.mint?.timestamp || a.mint?.blockTimestamp || '1970-01-01T00:00:00Z'
        bValue = b.mint?.timestamp || b.mint?.blockTimestamp || '1970-01-01T00:00:00Z'
        break
      case 'COLLECTION':
        aValue = a.contractMetadata?.name || a.contract?.name || ''
        bValue = b.contractMetadata?.name || b.contract?.name || ''
        break
      case 'NAME':
        aValue = a.title || a.name || ''
        bValue = b.title || b.name || ''
        break
      case 'ESTIMATED_VALUE':
        aValue = a.estimatedValue?.value || 0
        bValue = b.estimatedValue?.value || 0
        break
      default:
        aValue = a.acquiredAt?.blockTimestamp || a.acquiredAt?.timestamp || '1970-01-01T00:00:00Z'
        bValue = b.acquiredAt?.blockTimestamp || b.acquiredAt?.timestamp || '1970-01-01T00:00:00Z'
    }

    // Handle string comparisons (timestamps)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue).getTime()
      const bDate = new Date(bValue).getTime()

      // Only use the date values if they're valid numbers
      if (!isNaN(aDate) && !isNaN(bDate)) {
        aValue = aDate
        bValue = bDate
      }
    }

    // Handle numeric comparisons
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'ASC' ? aValue - bValue : bValue - aValue
    }

    // Fallback to string comparison if values aren't numbers
    const aStr = String(aValue || '')
    const bStr = String(bValue || '')
    return sortDirection === 'ASC'
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr)
  })

  debug('Sorting completed')

  // Debug: Show first few NFTs after sorting
  if (sortedNFTs.length > 0) {
    debug('First 3 NFTs after sorting:')
    sortedNFTs.slice(0, 3).forEach((nft, idx) => {
      const tokenId = getTokenId(nft)
              const title = nft.name || nft.title || 'unknown'
      const contract = nft.contractMetadata?.name || nft.contract?.name || 'unknown'
      let sortValue: string | number = ''

      switch (sortBy) {
        case 'TRANSFERTIME':
          sortValue = nft.acquiredAt?.blockTimestamp || nft.acquiredAt?.timestamp || 'unknown'
          break
        case 'TOKENID':
          sortValue = tokenId
          break
        case 'MINTTIME':
          sortValue = nft.mint?.timestamp || nft.mint?.blockTimestamp || 'unknown'
          break
        case 'COLLECTION':
          sortValue = nft.contractMetadata?.name || nft.contract?.name || 'unknown'
          break
        case 'NAME':
          sortValue = nft.name || nft.title || 'unknown'
          break
        case 'ESTIMATED_VALUE':
          sortValue = nft.estimatedValue?.value || 0
          break
      }

      debug(`  ${idx + 1}. ${title} (ID: ${tokenId}) from ${contract} - Sort value: ${sortValue}`)
    })
  }

  return sortedNFTs
}

const handleSortChange = () => {
  console.log('[NFT Sorting] Sort change triggered:', { sortBy: currentSortBy.value, sortDirection: currentSortDirection.value })
  debug('Input NFTs length:', props.nfts.length)
  
  const { sortBy, sortDirection } = validateSorting(currentSortBy.value, currentSortDirection.value)
  
  // Update the reactive values if validation changed them
  currentSortBy.value = sortBy
  currentSortDirection.value = sortDirection
  
  debug('Starting sort operation')
  emit('sort-start')
  
  const sortedNfts = sortNFTs(props.nfts, sortBy, sortDirection)
  debug('Sort result length:', sortedNfts.length)
  emit('sort-results', sortedNfts)
  
  debug('Sort operation completed')
  emit('sort-complete', `Sorted ${sortedNfts.length} NFTs by ${sortBy} ${sortDirection}`)
}

// Watch for NFT changes - but don't auto-sort to avoid loops
watch(() => props.nfts, (newNFTs, oldNFTs) => {
  debug('NFTs changed from', oldNFTs?.length || 0, 'to', newNFTs?.length || 0)
  // Don't auto-trigger sorting when NFTs change to avoid infinite loops
  // Sorting will only happen when user manually changes sort options
}, { deep: true })

// Initial sort - only if we have NFTs and sorting is enabled
// But don't auto-sort on component creation to avoid loops
// if (props.enableSorting && props.nfts.length > 0) {
//   handleSortChange()
// }

// Expose methods for external use
defineExpose({
  validateSorting,
  sortNFTs,
  isSingleCollection: () => isSingleCollection.value,
  getSortingOptions: () => availableSortOptions.value
})
</script>

<style scoped>
.nft-sorting {
  margin-bottom: 1rem;
}

.sorting-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.sort-label {
  font-weight: 500;
  color: #333;
  margin-right: 0.25rem;
}

.sort-select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  background: white;
  font-size: 0.875rem;
  min-width: 120px;
}

.sort-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.sort-select:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.sorting-debug {
  margin-top: 0.5rem;
  color: #6c757d;
  font-size: 0.75rem;
}
</style> 